// pages/Comedor.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,Empty,Skeleton,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Badge,
  Statistic,
  Card,
  Divider,
  Alert,
  Spin,
  Drawer,
  Grid,
  Avatar,
  List,
  FloatButton,
  Progress,
  ConfigProvider,
  Flex
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  TeamOutlined,
  ExportOutlined,
  CalendarOutlined,
  CoffeeOutlined,
  HomeOutlined,
  FilterOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  GroupOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Comedor = () => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [loadingCheckin, setLoadingCheckin] = useState(false);
  const [data, setData] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  // Estados para filtros y búsqueda
  const [searchText, setSearchText] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false); // Estado para expandir/colapsar filtros
  
  // Estados para fechas y servicios
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedService, setSelectedService] = useState('Desayuno');
  
  const [stats, setStats] = useState({
    total: 0,
    checkedIn: 0,
    pending: 0,
    habitaciones: 0
  });

  // Detectar tamaño de pantalla
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallMobile = screens.xs && !screens.sm;

  // Referencias
  const searchInputRef = useRef(null);

  // Función para determinar el servicio según la hora actual
  const getCurrentService = () => {
    const now = dayjs();
    const hour = now.hour();
    const minute = now.minute();
    const currentTime = hour + minute / 60;
    
    if (currentTime >= 1 && currentTime < 12) {
      return 'Desayuno';
    }
    else if (currentTime >= 12 && currentTime < 17) {
      return 'Almuerzo';
    }
    else if (currentTime >= 18 && currentTime < 24) {
      return 'Cena';
    }
    return 'Desayuno';
  };

  // Datos filtrados según quickFilter
  const filteredData = data.filter(item => {
    if (quickFilter === 'checked') return item.checkedIn;
    if (quickFilter === 'pending') return !item.checkedIn;
    return true;
  });

  // Función para realizar la búsqueda
  const handleSearch = () => {
    if (searchInputValue.trim()) {
      setSearchLoading(true);
      setSearchText(searchInputValue.trim());
      // setPagination(prev => ({ ...prev, current: 1 }));
      fetchComedorData({ current: 1, search: searchInputValue.trim() });
      setSearchInputValue('');
    } else {
      handleResetSearch();
    }
  };

  // Limpiar búsqueda
  const handleResetSearch = () => {
    setSearchText('');
    setSearchInputValue('');
    setQuickFilter('all');
    fetchComedorData();
  };

  // Limpiar búsqueda y hacer foco
  const handleClearSearch = () => {
    setSearchInputValue('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Función para obtener datos del comedor
  const fetchComedorData = async (params = {}) => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const searchParam = params.search !== undefined ? params.search : searchText;
    
    const response = await axios.get(`${BASE_URL}/api/comedor`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        fecha: selectedDate.format('DD/MM/YYYY'),
        servicio: selectedService,
        search: searchParam,
        page: 1,
        pageSize: 100000 // Traer todos los registros
      }
    });
    
    if (response.data.success) {
      const allData = response.data.data;
      setData(allData);
      
      // Calcular estadísticas directamente de los datos
      const checkedInCount = allData.filter(item => item.checkedIn).length;
      const habitacionesCount = new Set(allData.map(d => d.Habitacion)).size;
      
      setStats({
        total: allData.length,
        checkedIn: checkedInCount,
        pending: allData.length - checkedInCount,
        habitaciones: habitacionesCount
      });
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    message.error('Error de conexión');
  } finally {
    setLoading(false);
    setSearchLoading(false);
  }
};

  // Ver detalles del huésped
  const handleView = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/comedor/guest/detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          numReserva: record.NumReserva,
          linea: record.Linea,
          numOcupante: record.NumOcupante,
          fecha: selectedDate.format('DD/MM/YYYY'),
          servicio: selectedService
        }
      });
      if (response.data.success) {
        setSelectedGuest(response.data.data);
        if (isMobile) {
          setDrawerVisible(true);
        } else {
          setModalVisible(true);
        }
      }
    } catch (error) {
      message.error('Error al cargar detalles');
    }
  };

  // Toggle check-in/check-out
  const handleToggleCheckin = async (record) => {
  setLoadingCheckin(true);
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${BASE_URL}/api/comedor/checkin`,
      {
        numReserva: record.NumReserva,
        linea: record.Linea,
        numOcupante: record.NumOcupante,
        servicio: selectedService,
        fecha: selectedDate.format('DD/MM/YYYY'),
        checkIn: !record.checkedIn
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      // Actualizar datos locales
      const updatedData = data.map(item => {
        if (item.NumReserva === record.NumReserva && 
            item.Linea === record.Linea && 
            item.NumOcupante === record.NumOcupante) {
          return { ...item, checkedIn: !record.checkedIn };
        }
        return item;
      });
      
      setData(updatedData);
      
      // Recalcular estadísticas
      const checkedInCount = updatedData.filter(item => item.checkedIn).length;
      setStats({
        total: updatedData.length,
        checkedIn: checkedInCount,
        pending: updatedData.length - checkedInCount,
        habitaciones: new Set(updatedData.map(d => d.Habitacion)).size
      });
      
      message.success(!record.checkedIn ? '✅ Entrada registrada en comedor' : '✅ Salida registrada');
      
      if (isMobile) setDrawerVisible(false);
      else setModalVisible(false);
    }
  } catch (error) {
    message.error('Error al actualizar');
  } finally {
    setLoadingCheckin(false);
  }
};

  const handleRefresh = () => {
    fetchComedorData();
    message.success('Datos actualizados');
  };
  
  // Cambiar fecha
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  // Cambiar servicio
  const handleServiceChange = (service) => {
    setSelectedService(service);
  };

  // Efecto para cargar datos cuando cambia fecha o servicio
  useEffect(() => {
    fetchComedorData();
  }, [selectedDate, selectedService]);

  // Efecto para establecer servicio inicial según hora actual
  useEffect(() => {
    const currentService = getCurrentService();
    setSelectedService(currentService);
  }, []);

  // Columnas para desktop
  const desktopColumns = [
    {
      title: 'Estado',
      dataIndex: 'checkedIn',
      key: 'checkedIn',
      width: 110,
      render: (checkedIn) => (
        checkedIn ? (
          <Badge status="success" text="En comedor" />
        ) : (
          <Badge status="default" text="Pendiente" />
        )
      ),
      filters: [
        { text: 'En comedor', value: true },
        { text: 'Pendiente', value: false },
      ],
      onFilter: (value, record) => record.checkedIn === value,
    },
    {
      title: 'Habitación',
      dataIndex: 'Habitacion',
      key: 'Habitacion',
      width: 100,
      sorter: (a, b) => (a.Habitacion || 0) - (b.Habitacion || 0),
      render: (habitacion) => (
        <Tag color="blue" style={{ fontSize: 14, fontWeight: 'bold' }}>
          {habitacion}
        </Tag>
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 200,
      sorter: (a, b) => a.Nombre?.localeCompare(b.Nombre || '') || 0,
      render: (nombre) => <Text strong>{nombre}</Text>,
    },
    {
      title: 'Ocupantes',
      key: 'occupants',
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Tag>👤 {record.Ad}A</Tag>
          {record.Ni > 0 && <Tag color="green">👶 {record.Ni}N</Tag>}
          {record.Bb > 0 && <Tag color="orange">🍼 {record.Bb}B</Tag>}
        </Space>
      ),
    },
    {
      title: 'Régimen',
      dataIndex: 'Regimen',
      key: 'Regimen',
      width: 100,
      render: (regimen) => <Tag color="purple">{regimen}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title={record.checkedIn ? "Registrar salida" : "Registrar entrada"}>
            <Button
              type={record.checkedIn ? "default" : "primary"}
              icon={record.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
              size="small"
              onClick={() => handleToggleCheckin(record)}
              loading={loadingCheckin}
            >
              {record.checkedIn ? 'Salida' : 'Entrada'}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Columnas para móvil
  const mobileColumns = [
    {
      title: '',
      dataIndex: 'checkedIn',
      key: 'status',
      width: 45,
      render: (checkedIn) => (
        <div style={{ 
          width: 32, 
          height: 32, 
          borderRadius: 16, 
          background: checkedIn ? '#52c41a20' : '#ff4d4f20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {checkedIn ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
          ) : (
            <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
          )}
        </div>
      ),
    },
    {
      title: 'Huésped',
      dataIndex: 'Nombre',
      key: 'Nombre',
      render: (nombre, record) => (
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: 4 }}>
            {nombre}
          </div>
          <Flex gap={8} wrap="wrap">
            <Tag color="blue" style={{ margin: 0, fontSize: '11px', borderRadius: 12 }}>
              📍 Hab {record.Habitacion}
            </Tag>
            <Tag color="purple" style={{ margin: 0, fontSize: '11px', borderRadius: 12 }}>
              {record.Regimen}
            </Tag>
          </Flex>
          <Flex gap={12} style={{ marginTop: 4 }}>
            <span style={{ fontSize: '12px', color: '#666' }}>👤 {record.Ad}A</span>
            {record.Ni > 0 && <span style={{ fontSize: '12px', color: '#666' }}>👶 {record.Ni}N</span>}
            {record.Bb > 0 && <span style={{ fontSize: '12px', color: '#666' }}>🍼 {record.Bb}B</span>}
          </Flex>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 75,
      render: (_, record) => (
        <Button
          type={record.checkedIn ? "default" : "primary"}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleCheckin(record);
          }}
          loading={loadingCheckin}
          style={{ borderRadius: 20, minWidth: 60 }}
        >
          {record.checkedIn ? 'Salida' : 'Entrada'}
        </Button>
      ),
    },
  ];

  // Estadísticas
  const renderStats = () => {
  if (isMobile) {
    return (
      <Card 
        style={{ 
          marginBottom: 16, 
          borderRadius: 20,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 12]}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {stats.habitaciones}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <HomeOutlined /> Hab.
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {stats.total}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <TeamOutlined /> Total
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {stats.checkedIn}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <CheckCircleOutlined /> En comedor
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {stats.pending}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <ClockCircleOutlined /> Pend.
              </div>
            </div>
          </Col>
        </Row>
        <Progress 
          percent={stats.total ? (stats.checkedIn / stats.total * 100) : 0}
          showInfo={false}
          strokeColor="#52c41a"
          trailColor="rgba(255,255,255,0.3)"
          style={{ marginTop: 12 }}
        />
      </Card>
    );
  }
  
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col span={6}>
        <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
          <Statistic
            title="Habitaciones"
            value={stats.habitaciones}
            prefix={<HomeOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
          <Statistic
            title="Total huéspedes"
            value={stats.total}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
          <Statistic
            title="En comedor"
            value={stats.checkedIn}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
          <Statistic
            title="Pendientes"
            value={stats.pending}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
    </Row>
  );
};

  // Barra de búsqueda siempre visible
  const renderSearchBar = () => (
    <Card style={{ marginBottom: 16, borderRadius: 16 }}>
      <Flex gap={12} vertical={isMobile} align={isMobile ? 'stretch' : 'center'}>
        <div style={{ flex: 1 }}>
          <Input
            ref={searchInputRef}
            placeholder="Buscar por nombre o número de habitación..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            onClear={handleClearSearch}
            prefix={<SearchOutlined />}
            suffix={
              searchInputValue && (
                <CloseOutlined 
                  onClick={handleClearSearch}
                  style={{ cursor: 'pointer', color: '#999' }}
                />
              )
            }
            size="large"
            style={{ borderRadius: 24 }}
          />
        </div>
        <Flex gap={8}>
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={handleSearch}
            loading={searchLoading}
            size="large"
            style={{ borderRadius: 20 }}
          >
            Buscar
          </Button>
          <Button 
            icon={filtersExpanded ? <UpOutlined /> : <DownOutlined />} 
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            size="large"
            style={{ borderRadius: 20 }}
          >
            {filtersExpanded ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            size="large"
            style={{ borderRadius: 20 }}
          >
            {!isMobile && 'Actualizar'}
          </Button>
        </Flex>
      </Flex>
      
      {/* Filtros expandibles (Fecha y Servicio) */}
      {filtersExpanded && (
        <div style={{ marginTop: 16 }}>
          <Divider style={{ margin: '8px 0 16px 0' }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>📅 Fecha</Text>
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format="DD/MM/YYYY"
                  style={{ width: '100%', borderRadius: 20 }}
                  suffixIcon={<CalendarOutlined />}
                  size="large"
                />
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>🍽️ Servicio</Text>
                <Select
                  value={selectedService}
                  onChange={handleServiceChange}
                  style={{ width: '100%', borderRadius: 20 }}
                  size="large"
                  options={[
                    { value: 'Desayuno', label: '☕ Desayuno (01:00 - 11:00)' },
                    { value: 'Almuerzo', label: '🍽️ Almuerzo (12:00 - 16:00)' },
                    { value: 'Cena', label: '🌙 Cena (18:00 - 23:00)' },
                  ]}
                />
              </Space>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );

//   // Filtro rápido
//   const renderQuickFilter = () => (
//   <Card style={{ marginBottom: 16, borderRadius: 16 }}>
//     <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
//       <Space>
//         <Text strong>Filtro rápido:</Text>
//         <Segmented
//           value={quickFilter}
//           onChange={setQuickFilter}
//           options={[
//             { label: `Todos (${stats.total})`, value: 'all' },
//             { label: `En comedor (${stats.checkedIn})`, value: 'checked' },
//             { label: `Pendientes (${stats.pending})`, value: 'pending' },
//           ]}
//           size={isMobile ? "small" : "middle"}
//         />
//       </Space>
//       {searchText && (
//         <Tag 
//           closable 
//           onClose={handleResetSearch}
//           color="blue"
//           style={{ fontSize: 13 }}
//         >
//           Buscando: "{searchText}"
//         </Tag>
//       )}
//     </Flex>
//   </Card>
// );

  // Función para agrupar datos por habitación
const groupDataByRoom = (items) => {
  const grouped = {};
  items.forEach(item => {
    const roomKey = `${item.Habitacion}`;
    if (!grouped[roomKey]) {
      grouped[roomKey] = {
        habitacion: item.Habitacion,
        ocupantes: [],
        allCheckedIn: true,
        allPending: true,
        partialChecked: false
      };
    }
    grouped[roomKey].ocupantes.push(item);
  });
  
  // Calcular estados por habitación
  Object.keys(grouped).forEach(room => {
    const ocupantes = grouped[room].ocupantes;
    const checkedInCount = ocupantes.filter(o => o.checkedIn).length;
    const totalCount = ocupantes.length;
    
    grouped[room].allCheckedIn = checkedInCount === totalCount;
    grouped[room].allPending = checkedInCount === 0;
    grouped[room].partialChecked = checkedInCount > 0 && checkedInCount < totalCount;
    grouped[room].checkedInCount = checkedInCount;
    grouped[room].totalCount = totalCount;
  });
  
  return Object.values(grouped).sort((a, b) => a.habitacion - b.habitacion);
};

// Función para toggle grupal por habitación
const handleRoomToggleCheckin = async (roomData) => {
  const { habitacion, ocupantes, allCheckedIn, partialChecked } = roomData;
  
  if (partialChecked) {
    Modal.confirm({
      title: `Habitación ${habitacion} - Ocupación parcial`,
      content: (
        <div>
          <p>Esta habitación tiene {ocupantes.filter(o => o.checkedIn).length} de {ocupantes.length} personas en comedor.</p>
          <p>¿Qué acción deseas realizar?</p>
        </div>
      ),
      okText: 'Registrar todos',
      cancelText: 'Registrar solo pendientes',
      onOk: () => toggleAllGuestsInRoom(ocupantes, true),
      onCancel: () => togglePendingGuestsInRoom(ocupantes),
    });
  } else {
    const newCheckInState = !allCheckedIn;
    toggleAllGuestsInRoom(ocupantes, newCheckInState);
  }
};

const toggleAllGuestsInRoom = async (ocupantes, checkInState) => {
  setLoadingCheckin(true);
  let successCount = 0;
  let errorCount = 0;
  
  for (const guest of ocupantes) {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BASE_URL}/api/comedor/checkin`,
        {
          numReserva: guest.NumReserva,
          linea: guest.Linea,
          numOcupante: guest.NumOcupante,
          servicio: selectedService,
          fecha: selectedDate.format('DD/MM/YYYY'),
          checkIn: checkInState
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      successCount++;
    } catch (error) {
      errorCount++;
      console.error(`Error al procesar ${guest.Nombre}:`, error);
    }
  }
  
  if (successCount > 0) {
    // Actualizar datos locales
    const updatedData = data.map(item => {
      const guestToUpdate = ocupantes.find(g => 
        g.NumReserva === item.NumReserva && 
        g.Linea === item.Linea && 
        g.NumOcupante === item.NumOcupante
      );
      if (guestToUpdate) {
        return { ...item, checkedIn: checkInState };
      }
      return item;
    });
    
    setData(updatedData);
    
    // Recalcular estadísticas
    const checkedInCount = updatedData.filter(item => item.checkedIn).length;
    setStats({
      total: updatedData.length,
      checkedIn: checkedInCount,
      pending: updatedData.length - checkedInCount,
      habitaciones: new Set(updatedData.map(d => d.Habitacion)).size
    });
    
    message.success(
      `${successCount} ${successCount === 1 ? 'persona' : 'personas'} ${checkInState ? 'registradas en comedor' : 'salidas registradas'}${errorCount > 0 ? ` (${errorCount} errores)` : ''}`
    );
  } else {
    message.error('Error al procesar la operación');
  }
  
  setLoadingCheckin(false);
};

const togglePendingGuestsInRoom = async (ocupantes) => {
  const pendingGuests = ocupantes.filter(g => !g.checkedIn);
  if (pendingGuests.length === 0) {
    message.info('Todos los huéspedes ya están en comedor');
    return;
  }
  await toggleAllGuestsInRoom(pendingGuests, true);
};

// Vista agrupada por habitación (reemplazar renderContent)
const renderGroupedByRoom = () => {
  const groupedData = groupDataByRoom(filteredData);
  
  if (groupedData.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            {searchText 
              ? `No se encontró "${searchText}"` 
              : 'No hay huéspedes para este servicio'}
          </span>
        }
        style={{ marginTop: 40 }}
      >
        {searchText && (
          <Button type="link" onClick={handleResetSearch}>
            Limpiar búsqueda
          </Button>
        )}
      </Empty>
    );
  }
  
  return (
    <div style={{ padding: '8px 0' }}>
      <Flex gap={16} vertical>
        {groupedData.map((room) => (
          <Card
            key={room.habitacion}
            style={{
              borderRadius: 20,
              border: room.partialChecked ? '2px solid #faad14' : 
                     room.allCheckedIn ? '2px solid #52c41a' : '1px solid #e8e8e8',
              background: room.allCheckedIn ? '#f6ffed' : '#ffffff',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.3s'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Header de la habitación */}
            <div 
              style={{
                padding: '14px 20px',
                background: room.allCheckedIn ? '#d9f7be' : 
                           room.partialChecked ? '#fff7e6' : '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => handleRoomToggleCheckin(room)}
            >
              <Flex align="center" gap={12}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  background: room.allCheckedIn ? '#52c41a' : 
                             room.partialChecked ? '#faad14' : '#d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 20
                }}>
                  {room.allCheckedIn ? <CheckCircleOutlined /> : 
                   room.partialChecked ? <ClockCircleOutlined /> : 
                   <TeamOutlined />}
                </div>
                <div>
                  <Text strong style={{ fontSize: 18 }}>
                    Habitación {room.habitacion}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {room.checkedInCount}/{room.totalCount} personas en comedor
                    </Text>
                  </div>
                </div>
              </Flex>
              
              <Button
                type={room.allCheckedIn ? "default" : "primary"}
                danger={room.allCheckedIn}
                icon={room.allCheckedIn ? <CloseOutlined /> : <CheckOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRoomToggleCheckin(room);
                }}
                loading={loadingCheckin}
                size="middle"
                style={{ borderRadius: 20, fontWeight: 500 }}
              >
                {room.allCheckedIn ? 'Salir todos' : 
                 room.partialChecked ? 'Gestionar' : 'Entrar todos'}
              </Button>
            </div>
            
            {/* Lista de ocupantes */}
            <div style={{ padding: '12px 16px' }}>
              <Flex gap={12} vertical>
                {room.ocupantes.map((guest, idx) => (
                  <div 
                    key={`${guest.NumReserva}-${guest.Linea}-${guest.NumOcupante}`}
                    style={{
                      padding: '12px 16px',
                      background: guest.checkedIn ? '#f6ffed' : '#fff',
                      borderRadius: 12,
                      border: `1px solid ${guest.checkedIn ? '#b7eb8f' : '#f0f0f0'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      hover: { background: '#fafafa' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(guest);
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex vertical style={{ flex: 1 }}>
                        <Flex align="center" gap={8} wrap="wrap">
                          <Text strong style={{ fontSize: 15 }}>
                            {guest.Nombre}
                          </Text>
                          <Tag 
                            color={guest.checkedIn ? 'success' : 'default'}
                            icon={guest.checkedIn ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                            style={{ margin: 0, borderRadius: 12 }}
                          >
                            {guest.checkedIn ? 'En comedor' : 'Pendiente'}
                          </Tag>
                          {guest.TodoIncluido === 'X' && (
                            <Tag color="gold" style={{ borderRadius: 12 }}>Todo Incluido</Tag>
                          )}
                        </Flex>
                        <Flex gap={16} style={{ marginTop: 6 }}>
                          <span style={{ fontSize: 12, color: '#666' }}>
                            👤 {guest.Ad} Adulto{guest.Ad !== 1 ? 's' : ''}
                          </span>
                          {guest.Ni > 0 && (
                            <span style={{ fontSize: 12, color: '#666' }}>
                              👶 {guest.Ni} Niño{guest.Ni !== 1 ? 's' : ''}
                            </span>
                          )}
                          {guest.Bb > 0 && (
                            <span style={{ fontSize: 12, color: '#666' }}>
                              🍼 {guest.Bb} Bebé{guest.Bb !== 1 ? 's' : ''}
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: '#666' }}>
                            📋 {guest.Regimen}
                          </span>
                        </Flex>
                      </Flex>
                      
                      <Button
                        type={guest.checkedIn ? "text" : "link"}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCheckin(guest);
                        }}
                        loading={loadingCheckin}
                        icon={guest.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
                        style={{ 
                          color: guest.checkedIn ? '#ff4d4f' : '#52c41a',
                          minWidth: 70,
                          fontWeight: 500
                        }}
                      >
                        {guest.checkedIn ? 'Salida' : 'Entrada'}
                      </Button>
                    </Flex>
                  </div>
                ))}
              </Flex>
            </div>
          </Card>
        ))}
      </Flex>
    </div>
  );
};

// Modificar la función renderContent para usar la vista agrupada
const renderContent = () => {
  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active avatar paragraph={{ rows: 4 }} />
        <Skeleton active avatar paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />
      </div>
    );
  }
  
  return renderGroupedByRoom();
};

  // Detalle del huésped
  const renderGuestDetail = (isMobileView = false) => {
    if (!selectedGuest) return <Spin />;
    
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Avatar
            size={isMobileView ? 64 : 80}
            style={{ 
              backgroundColor: selectedGuest.checkedIn ? '#52c41a' : '#1890ff',
              marginBottom: 12
            }}
            icon={<UserOutlined />}
          />
          <Title level={isMobileView ? 5 : 4} style={{ marginBottom: 4 }}>
            {selectedGuest.Nombre}
          </Title>
          <Tag color="blue">Habitación {selectedGuest.Habitacion}</Tag>
        </div>
        
        <List
          size="small"
          dataSource={[
            { label: 'Estado', value: selectedGuest.checkedIn ? '✅ En comedor' : '⏳ Pendiente' },
            { label: 'Régimen', value: selectedGuest.Regimen },
            { label: 'Adultos', value: selectedGuest.Ad },
            { label: 'Niños', value: selectedGuest.Ni },
            { label: 'Bebés', value: selectedGuest.Bb },
            { label: 'Entrada', value: dayjs(selectedGuest.Entrada).format('DD/MM/YYYY') },
            { label: 'Salida', value: dayjs(selectedGuest.Salida).format('DD/MM/YYYY') },
            { label: 'Reserva', value: selectedGuest.NumReserva },
          ]}
          renderItem={(item) => (
            <List.Item style={{ padding: '12px 0' }}>
              <Flex justify="space-between" style={{ width: '100%' }}>
                <Text type="secondary">{item.label}</Text>
                <Text strong>{item.value}</Text>
              </Flex>
            </List.Item>
          )}
        />
        
        {(selectedGuest.TodoIncluido === 'X' || selectedGuest.BebidaIncluida === 'X') && (
          <>
            <Divider />
            {selectedGuest.TodoIncluido === 'X' && (
              <Alert
                message="Todo Incluido"
                description="Régimen todo incluido activo"
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
            {selectedGuest.BebidaIncluida === 'X' && (
              <Alert
                message="Bebida Incluida"
                description="Bebida incluida en el servicio"
                type="info"
                showIcon
              />
            )}
          </>
        )}
        
        <Divider />
        
        <Button
          type={selectedGuest.checkedIn ? "primary" : "primary"}
          danger={selectedGuest.checkedIn}
          icon={selectedGuest.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
          onClick={() => handleToggleCheckin(selectedGuest)}
          loading={loadingCheckin}
          size="large"
          block
          style={{ 
            borderRadius: 24,
            height: 48,
            fontSize: 16
          }}
        >
          {selectedGuest.checkedIn ? 'Registrar Salida' : 'Registrar Entrada'}
        </Button>
      </div>
    );
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: { borderRadiusLG: 20 },
          Button: { borderRadius: 20 },
          Tag: { borderRadiusSM: 12 },
        }
      }}
    >
      <PageContainer
        title={isSmallMobile ? "🍽️ Comedor" : "Comedor"}
        subtitle={!isSmallMobile && filtersExpanded ? `${selectedService} - ${selectedDate.format('DD/MM/YYYY')}` : undefined}
        icon={<TeamOutlined />}
        loading={loading}
        onRefresh={handleRefresh}
        // badgeText={`${pagination.pending} pendientes`}
        extra={
          !isMobile && (
            <Button icon={<ExportOutlined />} onClick={() => message.info('Exportación en desarrollo')}>
              Exportar
            </Button>
          )
        }
      >
        {/* Barra de búsqueda siempre visible */}
        {renderSearchBar()}
        
        
        {/* {renderQuickFilter()} */}
        
        {/* Estadísticas */}
        {renderStats()}
        
        {/* Tabla de huéspedes */}
        <Card style={{ borderRadius: 16 }}>
          {renderContent()}
        </Card>
        
        {/* Modal para desktop/tablet */}
        <Modal
          title="Detalle del huésped"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={500}
          centered
        >
          {renderGuestDetail()}
        </Modal>
        
        {/* Drawer para móvil */}
        <Drawer
          title="Detalle del huésped"
          placement="bottom"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          height="auto"
          style={{ borderRadius: '20px 20px 0 0' }}
          closeIcon={<CloseOutlined />}
        >
          {renderGuestDetail(true)}
        </Drawer>
        
        {/* FloatButton para móvil */}
        {isMobile && (
          <FloatButton.Group
            trigger="click"
            icon={<FilterOutlined />}
            style={{ bottom: 80, right: 16 }}
          >
            <FloatButton 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              tooltip="Actualizar"
            />
            <FloatButton 
              icon={<ExportOutlined />} 
              onClick={() => message.info('Exportación en desarrollo')}
              tooltip="Exportar"
            />
          </FloatButton.Group>
        )}
      </PageContainer>
    </ConfigProvider>
  );
};

// Componente Segmented (si no existe en tu versión de Ant Design)
const Segmented = ({ value, onChange, options, size, block, style }) => {
  return (
    <div style={{ display: 'flex', gap: 8, ...style }}>
      {options.map(option => {
        const isActive = value === option.value;
        return (
          <Button
            key={option.value}
            type={isActive ? "primary" : "default"}
            size={size}
            onClick={() => onChange(option.value)}
            style={{ 
              borderRadius: 20,
              ...(block ? { flex: 1 } : {})
            }}
          >
            {option.icon && <span style={{ marginRight: 4 }}>{option.icon}</span>}
            {option.label}
          </Button>
        );
      })}
    </div>
  );
};

export default Comedor;